import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../supabase';
import jsPDF from 'jspdf';
import '../styles/ViewExperiment.css';

export default function ViewExperiment() {
  const { id } = useParams();
  const [experiment, setExperiment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    async function getExperiment() {
      setLoading(true);
      const { data, error } = await supabase
        .from('experiments')
        .select('*')
        .eq('id', id)
        .single();
      if (error) {
        console.error('Error fetching experiment:', error);
        setLoading(false);
      } else {
        // Fetch the user profile for the experiment creator
        if (data.user_id) {
          const { data: profile, error: profileError } = await supabase
            .from('user_profiles')
            .select('full_name, email')
            .eq('id', data.user_id)
            .single();
          
          if (!profileError && profile) {
            setExperiment({ ...data, user_profiles: profile });
          } else {
            setExperiment(data);
          }
        } else {
          setExperiment(data);
        }
        setLoading(false);
      }
    }

    getExperiment();
  }, [id]);

  const getVolumeUsed = () => {
    if (!experiment) return 0;
    const { final_naoh_reading, initial_naoh_reading } = experiment;
    return (final_naoh_reading - initial_naoh_reading).toFixed(2);
  };

  const exportToPdf = () => {
    setExporting(true);
    const doc = new jsPDF();
    
    // Colors
    const primaryColor = [102, 126, 234];
    const darkColor = [26, 26, 46];
    const grayColor = [108, 117, 125];
    
    // Header
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, 210, 40, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont(undefined, 'bold');
    doc.text('KVM & Co.', 20, 20);
    doc.setFontSize(14);
    doc.setFont(undefined, 'normal');
    doc.text('FFA Test Report', 20, 30);
    
    // Report Date - use white/light color for visibility
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}`, 150, 30);
    
    // Sample Information Section
    let yPos = 55;
    doc.setTextColor(...darkColor);
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('Sample Information', 20, yPos);
    
    yPos += 10;
    doc.setDrawColor(230, 230, 230);
    doc.line(20, yPos - 3, 190, yPos - 3);
    
    const addField = (label, value, x = 20) => {
      doc.setFont(undefined, 'normal');
      doc.setTextColor(...grayColor);
      doc.setFontSize(10);
      doc.text(label, x, yPos);
      doc.setTextColor(...darkColor);
      doc.setFont(undefined, 'bold');
      doc.text(String(value || '-'), x + 50, yPos);
      yPos += 8;
    };
    
    addField('Sample ID:', experiment.sample_id);
    addField('Supplier Name:', experiment.supplier_name);
    addField('Supplier Location:', experiment.supplier_location);
    addField('Number of Bags:', experiment.number_of_bags);
    addField('Inspected Quality:', experiment.inspected_quality);
    addField('Supplied Date:', new Date(experiment.supplied_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }));
    addField('Experiment Date:', experiment.experiment_date ? 
      new Date(experiment.experiment_date).toLocaleString('en-GB', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }) : 
      new Date(experiment.created_at).toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }));
    addField('Operator:', experiment.operator_initials);
    
    // Test Parameters Section
    yPos += 10;
    doc.setTextColor(...darkColor);
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('Test Parameters', 20, yPos);
    
    yPos += 10;
    doc.line(20, yPos - 3, 190, yPos - 3);
    
    addField('Moisture %:', experiment.moisture_percentage ? `${experiment.moisture_percentage}%` : '-');
    addField('Oil Weight:', `${experiment.exact_oil_weight} g`);
    addField('Initial Reading:', `${experiment.initial_naoh_reading} mL`);
    addField('Final Reading:', `${experiment.final_naoh_reading} mL`);
    addField('Volume Used:', `${getVolumeUsed()} mL`);
    addField('NaOH Normality:', `${experiment.normality_of_naoh} N`);
    
    // Results Section
    yPos += 10;
    doc.setFillColor(...primaryColor);
    doc.rect(20, yPos - 5, 170, 25, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('FFA Result', 30, yPos + 8);
    doc.setFontSize(18);
    doc.text(`${experiment.calculated_ffa}%`, 150, yPos + 8, { align: 'right' });
    
    yPos += 35;
    
    // Formula
    doc.setTextColor(...grayColor);
    doc.setFontSize(10);
    doc.setFont(undefined, 'italic');
    doc.text('Formula: FFA % = (V × N × 28.2) / W', 20, yPos);
    yPos += 6;
    doc.setFontSize(9);
    doc.text('Where: V = Volume Used, N = Normality, W = Oil Weight', 20, yPos);
    
    // Notes Section
    if (experiment.notes) {
      yPos += 15;
      doc.setTextColor(...darkColor);
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text('Notes', 20, yPos);
      
      yPos += 10;
      doc.line(20, yPos - 3, 190, yPos - 3);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(...grayColor);
      doc.setFontSize(10);
      
      const lines = doc.splitTextToSize(experiment.notes, 170);
      lines.forEach((line) => {
        doc.text(line, 20, yPos);
        yPos += 6;
      });
    }
    
    // Footer
    doc.setDrawColor(230, 230, 230);
    doc.line(20, 270, 190, 270);
    doc.setTextColor(...grayColor);
    doc.setFontSize(8);
    doc.text('KVM & Co. FFA Tracker - Confidential Document', 105, 280, { align: 'center' });
    
    doc.save(`FFA_Report_${experiment.sample_id}_${new Date().toISOString().split('T')[0]}.pdf`);
    setExporting(false);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!experiment) {
    return (
      <div className="error-container">
        <p>Experiment not found</p>
        <Link to="/dashboard" className="btn btn-secondary">Back to Dashboard</Link>
      </div>
    );
  }

  return (
    <div className="experiment-container">
      <div className="experiment-header">
        <Link to="/dashboard" className="btn btn-secondary">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
          Back
        </Link>
        <div className="header-info">
          <h1 className="experiment-title">Experiment Report</h1>
          <span className="experiment-id">#{experiment.sample_id}</span>
        </div>
        <button onClick={exportToPdf} className="btn btn-success" disabled={exporting}>
            {exporting ? (
              <>
                <span className="small-loader"></span>
                <span>Exporting...</span>
              </>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
                <span>Export PDF</span>
              </>
            )}
        </button>
      </div>

      <div className="experiment-content">
        <div className="info-grid">
          <div className="info-card">
            <h2 className="section-title">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                <line x1="12" y1="22.08" x2="12" y2="12"></line>
              </svg>
              Sample Information
            </h2>
            <div className="info-content">
              <div className="info-item">
                <span className="info-label">Sample ID</span>
                <span className="info-value">{experiment.sample_id}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Supplier Name</span>
                <span className="info-value">{experiment.supplier_name}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Supplier Location</span>
                <span className="info-value">{experiment.supplier_location}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Number of Bags</span>
                <span className="info-value">{experiment.number_of_bags}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Inspected Quality</span>
                <span className="info-value">{experiment.inspected_quality}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Supplied Date</span>
                <span className="info-value">{new Date(experiment.supplied_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
              </div>
            </div>
          </div>

          <div className="info-card">
            <h2 className="section-title">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
              </svg>
              Test Parameters
            </h2>
            <div className="info-content">
              <div className="info-item">
                <span className="info-label">Moisture %</span>
                <span className="info-value">{experiment.moisture_percentage || '-'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Oil Weight (W)</span>
                <span className="info-value">{experiment.exact_oil_weight} g</span>
              </div>
              <div className="info-item">
                <span className="info-label">Initial NaOH Reading</span>
                <span className="info-value">{experiment.initial_naoh_reading} mL</span>
              </div>
              <div className="info-item">
                <span className="info-label">Final NaOH Reading</span>
                <span className="info-value">{experiment.final_naoh_reading} mL</span>
              </div>
              <div className="info-item">
                <span className="info-label">Volume Used (V)</span>
                <span className="info-value highlight">{getVolumeUsed()} mL</span>
              </div>
              <div className="info-item">
                <span className="info-label">NaOH Normality (N)</span>
                <span className="info-value">{experiment.normality_of_naoh} N</span>
              </div>
            </div>
          </div>
        </div>

        <div className="result-card">
          <div className="result-header">
            <h2 className="result-title">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
              </svg>
              FFA Test Result
            </h2>
          </div>
          <div className="result-content">
            <div className="result-value">
              <span className="result-number">{experiment.calculated_ffa}</span>
              <span className="result-unit">%</span>
            </div>
            <div className="formula-container">
              <p className="formula">FFA % = (V × N × 28.2) / W</p>
              <p className="formula-desc">
                = ({getVolumeUsed()} × {experiment.normality_of_naoh} × 28.2) / {experiment.exact_oil_weight}
              </p>
            </div>
          </div>
        </div>

        {experiment.notes && (
          <div className="notes-card">
            <h2 className="section-title">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10 9 9 9 8 9"></polyline>
              </svg>
              Notes
            </h2>
            <p className="notes-text">{experiment.notes}</p>
          </div>
        )}

        <div className="info-card">
          <h2 className="section-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
            Experiment Details
          </h2>
          <div className="info-content">
            <div className="info-item">
              <span className="info-label">Experiment Date & Time</span>
              <span className="info-value">{experiment.experiment_date ? 
                new Date(experiment.experiment_date).toLocaleString('en-GB', { 
                  day: '2-digit', 
                  month: '2-digit', 
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                }) : 
                new Date(experiment.created_at).toLocaleString('en-GB', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Operator Initials</span>
              <span className="info-value">{experiment.operator_initials}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Record Created</span>
              <span className="info-value">{new Date(experiment.created_at).toLocaleString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Created By</span>
              <span className="info-value">{experiment.user_profiles?.full_name || experiment.user_profiles?.email || 'Unknown User'}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}